import React, { useState, useMemo } from "react";
import { getAssessments } from "../hooks/usePreAss";
import { IQuestionCreation } from "../../../store/types/job";
import { Skeleton, Table, TableProps } from "antd";
import { useNavigate } from "react-router-dom";
import { delete_assessment } from "../../../services/api/job";
import { useAppDispatch } from "../../../store/hooks";
import DeleteConfirmCandidateModal from "../../../components/elements/DeleteConfirmCandidateModal";
import { FaTrashAlt, FaEdit } from "react-icons/fa";
import SearchInput from "../../../components/elements/forms/SearchInput";
import { BiSearchAlt } from "react-icons/bi";

const AssessmentsTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { assessments, loading } = getAssessments();

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    candidate: null as IQuestionCreation | null,
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Filter assessments based on search term
  const filteredAssessments = useMemo(() => {
    if (!searchTerm || !assessments) return assessments;

    const lowerSearch = searchTerm.toLowerCase();
    return assessments.filter(
      (assessment: any) =>
        assessment.jobTitle?.toLowerCase().includes(lowerSearch) ||
        assessment.description?.toLowerCase().includes(lowerSearch)
    );
  }, [assessments, searchTerm]);

  // Confirm delete
  const handleConfirmDelete = () => {
    if (deleteModal.candidate) {
      console.log("Confirmed delete for:", deleteModal.candidate);
      delete_assessment(dispatch, deleteModal.candidate.id, navigate);

      // Close modal
      setDeleteModal({ isOpen: false, candidate: null });
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    console.log("Delete cancelled");
    setDeleteModal({ isOpen: false, candidate: null });
  };

  const columns: TableProps<IQuestionCreation>["columns"] = [
    {
      title: "S/N",
      dataIndex: "key",
      width: 20,
      align: "center",
      render: (_, __, index) => index + 1,
      // rowScope: 'row',
    },
    {
      title: "Title",
      dataIndex: "jobTitle",
      key: "jobTitle",
      render: (_, record) =>
        record.jobTitle
          ?.replace(/_+/g, " ")
          .toLowerCase()
          ?.replace(/\b\w/g, (char) => char.toUpperCase()),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (_, record) =>
        record?.description?.length > 1
          ? record?.description?.substring(0, 50) + "..."
          : "",
    },
    // {
    //   title: "Duration",
    //   dataIndex: "duration",
    //   key: "duration",
    //   // render: (_, record) => record.duration,
    // },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      // width: 180,
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-3">
          <div
            onClick={() =>
              navigate(`/assessment-create?id=${record.id}&mode=edit`)
            }
            className="cursor-pointer text-green-600 hover:text-green-800"
          >
            <FaEdit className="text-[18px]" />
          </div>
          <div
            onClick={() => {
              console.log("Delete button clicked for:", record);
              setDeleteModal({
                isOpen: true,
                candidate: record,
              });
              // delete_job(dispatch, record.id);
            }}
            className="cursor-pointer"
          >
            <FaTrashAlt className="text-[red] text-[18px]" />
          </div>
        </div>
      ),
    },
  ];
  return (
    <div className="w-full py-5">
      <div className="max-w-[1096px] mx-auto px-3">
        <div className="flex border-b border-border text-20">
          {/* <div
            onClick={() => navigate(`/pretest-create`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[600]">Add Pretest Questions</p>
          </div>
          <div
            onClick={() => navigate(`/assessment-create`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[400]">Add Assessment Questions</p>
          </div> */}
          <div
            onClick={() => navigate(`/pretests`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[600]">Pretest Questions</p>
          </div>
          <div
            onClick={() => navigate(`/assessments`)}
            className="border-b-[3px] border-secondary pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[600]">Assessment Questions</p>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <p className="font-[600]">Newly Added Assessments</p>
          <div className="flex items-center gap-3">
            <SearchInput
              className="w-full max-w-[280px]"
              name="search"
              value={searchTerm}
              placeholder="Search by title..."
              icon={<BiSearchAlt size={18} color="#243677" />}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div
              className="h-[40px] w-[220px] flex items-center justify-center bg-secondary text-white rounded-8 cursor-pointer"
              onClick={() => navigate(`/assessment-create`)}
            >
              Add New Assessment
            </div>
          </div>
        </div>

        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : assessments ? (
          <Table<IQuestionCreation>
            dataSource={
              (filteredAssessments || []).map((c: any) => ({
                ...c,
                deletedAt: c.deletedAt ?? "",
              })) as IQuestionCreation[]
            }
            columns={columns}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} Assessments`,
            }}
          />
        ) : (
          <div className="text-center text-gray-500">
            Contents failed to load, please try again.
          </div>
        )}
      </div>
      <DeleteConfirmCandidateModal
        title="assessment"
        candidate={deleteModal.candidate}
        isOpen={deleteModal.isOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default AssessmentsTable;
